/* @HEADER
 * @version 1.0.67 | 2026-08-19
 * @purpose Choose between the plain and the version-naming update prompt, so an unknown version never renders as "(undefined)" (ZVX-DEV-4477).
 * @sidecar formatUpdateAvailable.mjs.header.md
 * @layer app | @hex _none_ | @ctx pwa
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-4477, ZVX-DEV-4423

/**
 * Operator request: the update prompt should say WHICH version it is offering,
 * so pressing Обновить is a decision rather than an act of faith — and so a
 * prompt that keeps reappearing can be told apart from one offering something
 * genuinely new.
 *
 * The component already knew the answer: `serverVersionRef` carries the version
 * the poll found (ZVX-DEV-4423). It was simply never shown.
 *
 * ## Why this is a decision and not a template
 *
 * The service-worker path can raise the prompt with NO version attached — that
 * is precisely why ZVX-DEV-4423's repeat-guard keeps a separate branch for it.
 * A plain template would then render "Доступна новая версия (undefined)", which
 * is worse than saying nothing: it looks like a defect to the person reading it
 * and tells them less than the sentence it replaced.
 *
 * Both sentences are supplied by the caller. All user-facing copy goes through
 * the messages layer; this module decides WHICH one, never what it says.
 */

/**
 * @param {{ plain?: string, versioned?: (version: string) => string, version?: string | null }} [input]
 * @returns {string}
 */
export function formatUpdateAvailable({ plain, versioned, version } = {}) {
  const fallback = typeof plain === 'string' ? plain : '';
  const trimmed = typeof version === 'string' ? version.trim() : '';

  if (trimmed.length === 0) return fallback;
  if (typeof versioned !== 'function') return fallback;

  try {
    const text = versioned(trimmed);
    return typeof text === 'string' && text.length > 0 ? text : fallback;
  } catch {
    // This renders inside a notice the person is already looking at; a throw
    // would replace an update prompt with a blank screen.
    return fallback;
  }
}
